/**
 * Product Lightbox
 * Feature: 019-product-page-carousel - Lightbox Enhancement
 *
 * Provides image zoom modal with:
 * - Focus trap for keyboard accessibility
 * - ESC key to close
 * - Click backdrop to close
 * - Background scroll lock
 * - High-resolution image display (2000px)
 */

class Lightbox {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.backdrop = container.querySelector('[data-lightbox-backdrop]');
    this.dialog = container.querySelector('[data-lightbox-dialog]');
    this.closeButton = container.querySelector('[data-lightbox-close]');
    this.image = container.querySelector('[data-lightbox-image]');

    this.isOpen = false;
    this.previousFocus = null;
    this.focusableElements = [];

    this.init();
  }

  /**
   * Initialize lightbox (T066)
   */
  init() {
    if (!this.container || !this.closeButton) return;

    // Close button click (T066)
    this.closeButton.addEventListener('click', () => this.close());

    // Backdrop click to close (T070)
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // ESC key handler (T069)
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Open lightbox (T067)
   * Show modal, set high-res image, lock scroll, focus first element
   */
  open(imageData) {
    if (this.isOpen || !imageData) return;

    // Store current focus to return to later
    this.previousFocus = document.activeElement;

    // Set high-resolution image (T067 - 2000px)
    if (this.image) {
      this.image.src = imageData.srcLarge || imageData.src;
      this.image.alt = imageData.alt || '';
    }

    // Show modal
    this.container.hidden = false;
    this.isOpen = true;

    // Lock background scroll (T067)
    this.lockScroll();

    // Setup focus trap (T068)
    this.setupFocusTrap();

    // Focus first focusable element (typically close button)
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  /**
   * Close lightbox (T069)
   * Hide modal, unlock scroll, return focus
   */
  close() {
    if (!this.isOpen) return;

    // Hide modal
    this.container.hidden = true;
    this.isOpen = false;

    // Unlock background scroll
    this.unlockScroll();

    // Return focus to previous element (T069)
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    // Clear image to save memory
    if (this.image) {
      this.image.src = '';
    }
  }

  /**
   * Setup focus trap (T068)
   * Query focusable elements, trap Tab key to cycle within modal
   * Matches mega-menu pattern from feature 015
   */
  setupFocusTrap() {
    if (!this.dialog) return;

    // Query all focusable elements within dialog
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(
      this.dialog.querySelectorAll(focusableSelectors)
    );

    // Trap Tab key (T068)
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || this.focusableElements.length === 0) return;

      const firstElement = this.focusableElements[0];
      const lastElement = this.focusableElements[this.focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab (backward)
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab (forward)
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  /**
   * Lock background scroll (T067)
   */
  lockScroll() {
    // Save current scroll position
    this.scrollY = window.scrollY;

    // Lock scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
  }

  /**
   * Unlock background scroll
   */
  unlockScroll() {
    // Restore scroll position
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position
    window.scrollTo(0, this.scrollY || 0);
  }
}

/**
 * Initialize lightbox on page load and connect to carousel (T071)
 */
document.addEventListener('DOMContentLoaded', function() {
  const lightboxContainer = document.querySelector('[data-lightbox]');

  if (lightboxContainer) {
    // Initialize lightbox
    const lightbox = new Lightbox(lightboxContainer);

    // Make accessible globally
    window.productLightbox = lightbox;

    // Connect to carousel main image (T071)
    const mainImage = document.querySelector('[data-main-image]');
    const lightboxTrigger = document.querySelector('[data-lightbox-trigger]');

    if (lightboxTrigger && mainImage) {
      lightboxTrigger.addEventListener('click', () => {
        // Get current image data from carousel
        const carousel = window.productCarousel;
        if (carousel && carousel.mediaData) {
          const currentMedia = carousel.mediaData.media[carousel.currentIndex];
          if (currentMedia && currentMedia.media_type === 'image') {
            lightbox.open(currentMedia);
          }
        }
      });
    }
  }
});

/**
 * Export for module systems (if needed)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Lightbox;
}
